import {
	createContext,
	lazy,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { MosaicWindowWrapper } from "@/components/layout/MosaicWrapper";
import { Navigation } from "@/components/layout/Navigation";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/components/ui/tabs.tsx";
import { Backtest } from "@/components/widgets/Backtest/ui/Backtest.tsx";
import { Chart } from "@/components/widgets/Chart/ui/Chart";
import { Filings } from "@/components/widgets/Filings/Filings";
import { Financials } from "@/components/widgets/Financials";
import { Holders } from "@/components/widgets/Holders/Holders";
import { Markets } from "@/components/widgets/Markets/ui/Markets";
import { Monitor } from "@/components/widgets/Monitor/ui/Monitor";
import { News } from "@/components/widgets/News/ui/News";
import { NotAuthenticatedWidget } from "@/components/widgets/NotAuthenticatedWidget";
import { PeadWidget } from "@/components/widgets/Pead/ui/Pead";
import { Profile } from "@/components/widgets/Profile/Profile.tsx";
import { Quote } from "@/components/widgets/Quote";
import { QuoteType } from "@/components/widgets/Quote/type";
import { Revision } from "@/components/widgets/Revision";
import { Screener } from "@/components/widgets/Screener";
import { Signals } from "@/components/widgets/Signals/Signals.tsx";
import { useAuth } from "@/hooks/useAuth";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import type { ViewId } from "@/shared/types/common";
import { FilterDataProvider } from "../FilterDataContext";
import type { WidgetState } from "../tabs/types";
import { WidgetViewProvider } from "../WidgetViewContext";

const StreamChat = lazy(() => import("@/components/widgets/StreamChat"));

export type UpdateState = (targetId: ViewId) => void;

export interface ElementsState {
	ticker?: string;
	activeTabElements?: "Profile" | "Signals" | "Filings";
	color?: string;
}

interface WidgetParams {
	state: WidgetState;
	eventEmitter: EventEmitter;
	width?: number;
	tileId?: ViewId;
	activeTabId?: string;
	onStateChange?: OnStateChange;
	updateState?: UpdateState;
}

export type TitleMap = Partial<
	Record<ViewId, (widgetParams: WidgetParams) => React.JSX.Element>
>;

const TitleMapContext = createContext<
	| {
			titleMap: TitleMap;
			setTitleMap: React.Dispatch<React.SetStateAction<TitleMap>>;
	  }
	| undefined
>(undefined);

export const TitleMapProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [titleMap, setTitleMap] = useState<TitleMap>({});
	const { isAuthenticated } = useAuth();

	useEffect(() => {
		setTitleMap({
			Elements: ({ state }) => (
				<Tabs defaultValue="profile" className="h-full">
					<TabsList>
						<TabsTrigger value="profile">Profile</TabsTrigger>
						<TabsTrigger value="signals">Signals</TabsTrigger>
						<TabsTrigger value="filings">Filings</TabsTrigger>
					</TabsList>

					<TabsContent value="profile">
						<MosaicWindowWrapper>
							<Profile state={state} />
						</MosaicWindowWrapper>
					</TabsContent>

					<TabsContent value="signals">
						<MosaicWindowWrapper className="grid-container">
							<Signals />
						</MosaicWindowWrapper>
					</TabsContent>

					<TabsContent value="filings" className="h-full max-h-filings">
						<MosaicWindowWrapper>
							<Filings state={state} />
						</MosaicWindowWrapper>
					</TabsContent>
				</Tabs>
			),
			Chart: ({ state, activeTabId, onStateChange }) => (
				<MosaicWindowWrapper>
					<Chart
						state={state}
						onStateChange={onStateChange}
						key={activeTabId}
					/>
				</MosaicWindowWrapper>
			),
			Screener: ({
				state,
				eventEmitter,
				width,
				tileId,
				activeTabId,
				onStateChange,
			}) => (
				<WidgetViewProvider>
					<FilterDataProvider>
						<MosaicWindowWrapper>
							<Screener
								state={state}
								width={width}
								tileId={tileId || "Screener"}
								includeTotals={true}
								eventEmitter={eventEmitter}
								key={activeTabId}
								onStateChange={onStateChange}
								isSubToolbarShown={true}
							/>
						</MosaicWindowWrapper>
					</FilterDataProvider>
				</WidgetViewProvider>
			),
			"Earnings Analysis": ({ state, tileId, onStateChange }) =>
				isAuthenticated ? (
					<MosaicWindowWrapper>
						<Revision
							state={state}
							tileId={tileId || "Earnings Analysis"}
							onStateChange={onStateChange}
						/>
					</MosaicWindowWrapper>
				) : (
					<NotAuthenticatedWidget />
				),
			Monitor: ({
				state,
				eventEmitter,
				width,
				tileId,
				activeTabId,
				onStateChange,
			}) => (
				<WidgetViewProvider>
					<MosaicWindowWrapper>
						<Monitor
							state={state}
							width={width}
							tileId={tileId || "Monitor"}
							eventEmitter={eventEmitter}
							key={activeTabId}
							onStateChange={onStateChange}
						/>
					</MosaicWindowWrapper>
				</WidgetViewProvider>
			),
			News: ({ state, eventEmitter, tileId }) => (
				<MosaicWindowWrapper>
					<News
						state={state}
						tileId={tileId || "News"}
						eventEmitter={eventEmitter}
					/>
				</MosaicWindowWrapper>
			),
			Holders: ({ state }) =>
				isAuthenticated ? (
					<Holders state={state} />
				) : (
					<NotAuthenticatedWidget />
				),

			Backtest: () => (
				<WidgetViewProvider>
					<MosaicWindowWrapper>
						<Backtest />
					</MosaicWindowWrapper>
				</WidgetViewProvider>
			),
			"Drift Forecast": ({ state, tileId }) =>
				isAuthenticated ? (
					<MosaicWindowWrapper>
						<PeadWidget widgetId={tileId || "Drift Forecast"} state={state} />
					</MosaicWindowWrapper>
				) : (
					<NotAuthenticatedWidget />
				),
			Financials: ({ state }) => (
				<WidgetViewProvider>
					<MosaicWindowWrapper>
						<Financials state={state} />
					</MosaicWindowWrapper>
				</WidgetViewProvider>
			),
			Markets: ({ tileId, onStateChange }) => (
				<MosaicWindowWrapper>
					<Markets tileId={tileId || "Markets"} onStateChange={onStateChange} />
				</MosaicWindowWrapper>
			),
			Quote: ({ state, tileId, onStateChange }) => (
				<MosaicWindowWrapper>
					<Quote
						tileId={tileId || "Quote"}
						state={state}
						onStateChange={onStateChange}
					/>
				</MosaicWindowWrapper>
			),
			"Advanced Quote": ({ state, tileId, onStateChange }) => (
				<MosaicWindowWrapper>
					<Quote
						tileId={tileId || "Advanced Quote"}
						state={{ ...state, quoteType: QuoteType.ADVANCED }}
						onStateChange={onStateChange}
					/>
				</MosaicWindowWrapper>
			),
			Navigation: ({ updateState }) => (
				<MosaicWindowWrapper>
					<Navigation updateState={updateState!} />
				</MosaicWindowWrapper>
			),
			Chat: () => (
				<MosaicWindowWrapper>
					<StreamChat />
				</MosaicWindowWrapper>
			),
		});
	}, [isAuthenticated]);

	return (
		<TitleMapContext.Provider value={{ titleMap, setTitleMap }}>
			{children}
		</TitleMapContext.Provider>
	);
};

export const useTitleMap = () => {
	const context = useContext(TitleMapContext);

	if (!context) {
		throw new Error("useTitleMap must be used within a TitleMapProvider");
	}
	return context;
};
